/**
 * Registration Form component for trAInedup.ai
 * Handles new user registration with validation
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, KeyRound } from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Card } from '../common/Card';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';
import { redirectToCheckout } from '../../services/stripe/subscriptions';
import { SUBSCRIPTION_TIERS, SubscriptionTier } from '../../services/stripe/config';
import { useAffiliateTracking } from '../../hooks/useAffiliateTracking';
import { normalizeActivationCodeInput } from '../../utils/activationCodes';
import { redeemActivationCode, validateActivationCode } from '../../services/activationCodes';

/**
 * Registration form component with validation and terms acceptance
 */
export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, loginWithGoogleAuth } = useAuth();
  const { getReferralCode } = useAffiliateTracking();

  // Get plan from query string
  const searchParams = new URLSearchParams(location.search);
  const plan = searchParams.get('plan');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [activationValidation, setActivationValidation] = useState<{ status: 'idle' | 'validating' | 'valid' | 'invalid'; message?: string }>({ status: 'idle' });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Detect if the visitor just completed the hero assessment and has a plan waiting
  const hasPendingPlan = React.useMemo(() => {
    try {
      return !!sessionStorage.getItem('heroAssessmentAnswers');
    } catch {
      return false;
    }
  }, []);

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name validation
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Terms acceptance validation
    if (!acceptTerms) {
      errors.terms = 'You must accept the Terms of Service and Privacy Policy';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getActivationErrorMessage = (reason?: string) => {
    switch (reason) {
      case 'expired':
        return 'This activation code has expired.';
      case 'disabled':
        return 'This activation code has been disabled.';
      case 'already_redeemed':
      case 'redeemed':
        return 'This activation code has already been used.';
      case 'existing_paid_plan':
        return 'Activation codes are only available for accounts without an active paid plan.';
      case 'not_found':
      case 'missing_code':
        return 'Invalid activation code. Please check the code and try again.';
      default:
        return 'We were unable to validate that activation code. Please try again.';
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
    
    // Clear general error
    if (error) {
      setError('');
    }
  };

  const handleActivationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeActivationCodeInput(e.target.value);
    setActivationCode(normalized);
    if (activationValidation.status !== 'idle') {
      setActivationValidation({ status: 'idle' });
    }
    if (error) {
      setError('');
    }
  };

  useEffect(() => {
    if (!activationCode) {
      setActivationValidation({ status: 'idle' });
      return () => undefined;
    }

    let cancelled = false;
    setActivationValidation((prev) => ({ ...prev, status: 'validating', message: 'Validating activation code...' }));
    const timeout = setTimeout(async () => {
      try {
        const validation = await validateActivationCode(activationCode);
        if (cancelled) return;
        if (validation.valid) {
          setActivationValidation({ status: 'valid', message: 'Activation code accepted for Stackcommerce lifetime access.' });
        } else {
          setActivationValidation({ status: 'invalid', message: getActivationErrorMessage(validation.reason) });
        }
      } catch (err) {
        if (!cancelled) {
          setActivationValidation({ status: 'invalid', message: 'Unable to validate activation code. Please try again.' });
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [activationCode]);

  const ensureActivationCodeIsValid = async (): Promise<boolean> => {
    const normalized = normalizeActivationCodeInput(activationCode);
    if (!normalized) return true;

    setActivationValidation({ status: 'validating', message: 'Validating activation code...' });
    try {
      const validation = await validateActivationCode(normalized);
      if (validation.valid) {
        setActivationValidation({ status: 'valid', message: 'Activation code accepted for Stackcommerce lifetime access.' });
        return true;
      }
      const message = getActivationErrorMessage(validation.reason);
      setActivationValidation({ status: 'invalid', message });
      setError(message);
      return false;
    } catch (err) {
      const message = 'Unable to validate activation code. Please try again.';
      setActivationValidation({ status: 'invalid', message });
      setError(message);
      return false;
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError('');

    const normalizedActivationCode = normalizeActivationCodeInput(activationCode);
    const activationOk = await ensureActivationCodeIsValid();
    if (!activationOk) {
      setIsLoading(false);
      return;
    }

    try {
      const referralCode = getReferralCode();
      const intendedDestination = (location.state as any)?.from?.pathname;
      
      const result = await register({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email,
        password: formData.password,
        referralCode,
        intendedDestination,
      });
      if (result.success) {
        if (normalizedActivationCode) {
          try {
            await redeemActivationCode(normalizedActivationCode);
          } catch (redeemErr: any) {
            const redeemMessage = redeemErr?.message || 'Failed to redeem activation code. You can try again from Billing after signing in.';
            setError(redeemMessage);
            alert(redeemMessage);
          }
        }
        if (plan) {
          await handlePostRegistrationCheckout();
          return;
        }
        // Redirect to email verification sent page
        navigate(ROUTES.emailVerificationSent);
      } else {
        setError(result.error || 'Failed to create account. Please try again.');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Google OAuth registration
   */
  const handleGoogleRegister = async () => {
    if (!acceptTerms) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);
    setError('');

    const normalizedActivationCode = normalizeActivationCodeInput(activationCode);
    const activationOk = await ensureActivationCodeIsValid();
    if (!activationOk) {
      setIsLoading(false);
      return;
    }

    try {
      const referralCode = getReferralCode();
      const result = await loginWithGoogleAuth(referralCode);
      if (result.success && result.user) {
        if (normalizedActivationCode) {
          try {
            await redeemActivationCode(normalizedActivationCode);
          } catch (redeemErr: any) {
            const redeemMessage = redeemErr?.message || 'Failed to redeem activation code. You can try again from Billing after signing in.';
            setError(redeemMessage);
            alert(redeemMessage);
          }
        }
        if (plan) {
          await handlePostRegistrationCheckout();
          return;
        }
        
        // Hero assessment completed before registering → show their plan
        if (hasPendingPlan || result.user.skillsAssessmentCompleted) {
          navigate(ROUTES.learningPlan);
        } else {
          // Cold signup (no assessment yet) → skills assessment
          navigate(ROUTES.skillsAssessment);
        }
      } else if (result.error) {
        setError(result.error);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to sign up with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle post-registration Stripe checkout
   */
  const handlePostRegistrationCheckout = async () => {
    if (!plan) return;

    // Normalize plan to subscription tier or route
    const planUpper = plan.toUpperCase();
    if (planUpper === 'CORPORATE' || planUpper === 'ENTERPRISE') {
      navigate(ROUTES.corporateRequest);
      return;
    }

    let tierKey: SubscriptionTier = SUBSCRIPTION_TIERS.STANDARD;
    if (planUpper === 'PREMIUM') tierKey = SUBSCRIPTION_TIERS.PREMIUM;

    try {
      const { getPriceIdFromTier } = await import('../../services/stripe/subscriptions');
      const priceId = getPriceIdFromTier(tierKey);
      await redirectToCheckout(priceId, tierKey);
    } catch (err) {
      navigate(ROUTES.dashboard);
    }
  };

  /**
   * Password strength indicator
   */
  const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score < 3) return { score, label: 'Weak', color: 'text-red-500' };
    if (score < 5) return { score, label: 'Medium', color: 'text-yellow-500' };
    return { score, label: 'Strong', color: 'text-green-500' };
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const activationHelperText = activationValidation.status === 'validating'
    ? 'Validating activation code...'
    : activationValidation.status === 'valid'
      ? activationValidation.message
      : undefined;

  return (
    <Card className="w-full max-w-md mx-auto">
      {/* Plan-waiting banner — shown when the visitor arrives from the hero assessment */}
      {hasPendingPlan && (
        <div
          className="rounded-xl p-4 mb-6 flex items-start gap-3"
          style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #f0fdfa 100%)', border: '1px solid #c7d2fe' }}
        >
          <span className="text-xl flex-shrink-0">🎯</span>
          <div>
            <p className="font-montserrat font-bold text-sm text-neutral-800">
              Almost there — your plan is waiting
            </p>
            <p className="font-open-sans text-xs text-neutral-600 mt-0.5">
              Create your free account in 30 seconds to unlock your personalised AI learning plan.
            </p>
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h2 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">
          {hasPendingPlan ? 'Create Your Free Account' : 'Create Your Account'}
        </h2>
        <p className="text-neutral-600">
          {hasPendingPlan ? 'Your personalised AI learning plan is ready' : "It's not too late to catch up, start today"}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Registration Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* First Name Input */}
        <Input
          label="First Name"
          name="firstName"
          type="text"
          value={formData.firstName}
          onChange={handleInputChange}
          icon={User}
          placeholder="Enter your first name"
          required
          fullWidth
          error={validationErrors.firstName}
        />

        {/* Last Name Input */}
        <Input
          label="Last Name"
          name="lastName"
          type="text"
          value={formData.lastName}
          onChange={handleInputChange}
          icon={User}
          placeholder="Enter your last name"
          required
          fullWidth
          error={validationErrors.lastName}
        />

        <Input
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          icon={Mail}
          placeholder="Enter your email"
          required
          disabled={isLoading}
          error={validationErrors.email}
        />

        <Input
          label="Activation Code (optional)"
          name="activationCode"
          type="text"
          value={activationCode}
          onChange={handleActivationCodeChange}
          icon={KeyRound}
          placeholder="Enter your activation code"
          fullWidth
          disabled={isLoading}
          error={activationValidation.status === 'invalid' ? activationValidation.message : undefined}
          helperText={activationHelperText}
        />

        <div className="relative">
          <Input
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            icon={Lock}
            placeholder="Create a password"
            required
            disabled={isLoading}
            error={validationErrors.password}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-neutral-400 hover:text-neutral-600 transition-colors duration-200"
            disabled={isLoading}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
          
          {/* Password Strength Indicator */}
          {formData.password && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-neutral-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score < 3 ? 'bg-red-500' :
                      passwordStrength.score < 5 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${passwordStrength.color}`}>
                  {passwordStrength.label}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            icon={Lock}
            placeholder="Confirm your password"
            required
            disabled={isLoading}
            error={validationErrors.confirmPassword}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-neutral-400 hover:text-neutral-600 transition-colors duration-200"
            disabled={isLoading}
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {/* Terms and Conditions */}
        <div className="space-y-4">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-1 rounded border-neutral-300 text-primary focus:ring-primary-500"
              disabled={isLoading}
              required
              aria-describedby="terms-helper"
            />
            <span className="text-sm text-neutral-600">
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-600 underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-600 underline">
                Privacy Policy
              </Link>
            </span>
          </label>
          
          {validationErrors.terms && (
            <p id="terms-helper" className="text-red-600 text-sm">{validationErrors.terms}</p>
          )}
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>

      {/* Divider */}
      <div className="my-6 flex items-center">
        <div className="flex-1 border-t border-neutral-200"></div>
        <span className="px-4 text-sm text-neutral-500">or</span>
        <div className="flex-1 border-t border-neutral-200"></div>
      </div>

      {/* Google Registration */}
      <Button
        variant="outline"
        size="lg"
        fullWidth
        onClick={handleGoogleRegister}
        disabled={isLoading}
        className="mb-6"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Continue with Google
      </Button>

      {/* Sign In Link */}
      <div className="text-center">
        <p className="text-neutral-600">
          Already have an account?{' '}
          <Link
            to={ROUTES.login}
            className="text-primary hover:text-primary-600 font-medium transition-colors duration-200"
          >
            Sign in
          </Link>
        </p>
      </div>
    </Card>
  );
};
